import { Button, Drawer, Link, Stack, Typography } from '@mui/material'
import { DataGrid, type GridColumns, GridOverlay } from '@mui/x-data-grid'
import React, { useMemo } from 'react'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import { AvailableClusterNamespaceAccessDrawerContent } from 'components/configurations/clusters/AvailableClusterNamespaceAccessDrawerContent'
import {
  CLUSTER_LIST_PAGE_SIZE,
  ShareableListType
} from 'components/configurations/clusters/clusters-utils'
import { useDrawerControlForDataGrid } from 'hooks/use-drawer-control-for-data-grid'
import { useManageSharedAssets } from 'hooks/use-manage-shared-assets'
import {
  defaultColumnProps,
  defaultGridProps,
  getGridStyles
} from 'utils/mui-components/data-grid-utils'

import type { AvailableK8sClusterAsset } from 'types/api/sharedAssets/sharedAssets'

function getEmptyStateMessage(
  isFiltered: boolean,
  shareableListType: ShareableListType
): string {
  if (shareableListType === ShareableListType.SHARED) {
    return isFiltered
      ? "We couldn't find any clusters shared with your team that match the search criteria"
      : "We couldn't find any clusters that are shared with your team"
  }

  return isFiltered
    ? "We couldn't find any clusters available to share with your team that match the search criteria"
    : "We couldn't find any clusters that are available to share with your team"
}

const ShareableClusterListEmptyState: React.FC<{
  isFiltered: boolean
  shareableListType: ShareableListType
}> = ({ isFiltered, shareableListType }) => {
  const message = getEmptyStateMessage(isFiltered, shareableListType)

  return (
    <GridOverlay>
      <Typography>{message}</Typography>
    </GridOverlay>
  )
}

export const ShareableClusterList: React.FC<{
  availableClusters: AvailableK8sClusterAsset[]
  isFiltered: boolean
  shareableListType: ShareableListType
}> = ({ availableClusters, isFiltered, shareableListType }) => {
  const { isOpen, activeItem, handleRowClick, handleOpen, handleClose } =
    useDrawerControlForDataGrid<{ assetId: string }>()

  const { getAvailableClusterById } = useManageSharedAssets()

  const columns: GridColumns<AvailableK8sClusterAsset> = [
    {
      ...defaultColumnProps,
      field: 'isOutdated',
      headerName: 'Status',
      flex: 0.75,
      renderCell: ({ row }) => {
        const { isOutdated } = row

        return (
          <Typography
            color={isOutdated ? 'error.main' : 'success.main'}
            variant="inherit"
          >
            {isOutdated ? 'Outdated' : 'Active'}
          </Typography>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'clusterName',
      headerName: 'Cluster',
      flex: 2,
      renderCell: ({ row }) => {
        const { assetId, clusterName } = row

        return (
          <Link onClick={() => handleOpen({ assetId })}>
            <TruncatedTextWithTooltip
              tooltipProps={{ title: clusterName }}
              variant="inherit"
            >
              {clusterName}
            </TruncatedTextWithTooltip>
          </Link>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'owningTeamName',
      headerName: 'Owning Team',
      flex: 2,
      renderCell: ({ row }) => {
        const { owningTeamName } = row

        return (
          <TruncatedTextWithTooltip
            tooltipProps={{ title: owningTeamName }}
            variant="inherit"
          >
            {owningTeamName}
          </TruncatedTextWithTooltip>
        )
      }
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: 'Actions',
      flex: 1.25,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" sx={{ columnGap: 1 }}>
            <Button
              sx={{ width: 'fit-content' }}
              variant="contained"
              onClick={() => handleOpen({ assetId: row.assetId })}
            >
              Manage Requests
            </Button>
          </Stack>
        )
      }
    }
  ]

  const activeCluster = useMemo(() => {
    if (!activeItem?.assetId) {
      return
    }

    return getAvailableClusterById(activeItem.assetId)
  }, [activeItem, getAvailableClusterById])

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        PaperProps={{
          sx: {
            padding: 6,
            height: '100%',
            width: 800
          }
        }}
        onClose={handleClose}
      >
        {activeCluster && (
          <AvailableClusterNamespaceAccessDrawerContent
            activeCluster={activeCluster}
            handleCloseDrawer={handleClose}
          />
        )}
      </Drawer>
      <DataGrid
        {...defaultGridProps}
        columns={columns}
        components={{
          NoRowsOverlay: () => (
            <ShareableClusterListEmptyState
              isFiltered={isFiltered}
              shareableListType={shareableListType}
            />
          )
        }}
        getRowId={(value) => value.assetId}
        pageSize={CLUSTER_LIST_PAGE_SIZE}
        rows={availableClusters}
        rowsPerPageOptions={[CLUSTER_LIST_PAGE_SIZE]}
        sx={getGridStyles()}
        onRowClick={handleRowClick}
      />
    </>
  )
}
